import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { QuantityBillingItemEditModal } from "./QuantityBillingItemEditModal";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatMoney } from "../../../utils/formatMoney";
import { formatDate, toDateString } from "../../../utils/dateUtils";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BASE_INCLUDED_DEVICES } from "@KassaroAB/shared/types/availableBillingItem";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { useGetStoreAvailableBillingItemsQuery } from "../../../query/availableBillingItemsQuery";
import { getQuantityOnDate, getQuantityTimeline } from "../../../utils/billingItemSegments";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const SummaryValue = styled.div`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const SummaryMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const BreakdownList = styled.div`
  margin: ${spacing.x1}px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const BreakdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const BreakdownValue = styled.span`
  color: ${colors.text};
  font-weight: 500;
  white-space: nowrap;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

interface ExtraDevicesSectionProps {
  billingItems: RestBillingItem[] | undefined;
  startDate: string;
}

export const ExtraDevicesSection = ({
  billingItems,
  startDate,
}: ExtraDevicesSectionProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const today = toDateString(new Date());
  const [isChangeOpen, setIsChangeOpen] = useState(false);

  const { data: availableBillingItems, isLoading } =
    useGetStoreAvailableBillingItemsQuery(storeId ?? null);

  const extraDeviceItem =
    availableBillingItems?.find(
      (item) => item.type === BillingItemType.EXTRA_DEVICE
    ) ?? null;
  const extraDeviceBillingItems = (billingItems ?? []).filter(
    (item) => item.availableBillingItemId === extraDeviceItem?.id
  );
  const extraDeviceBillingItem =
    extraDeviceBillingItems.find(
      (item) => item.segments && item.segments.length > 0
    ) ??
    extraDeviceBillingItems[0] ??
    null;

  if (isLoading) {
    return (
      <Card>
        <SectionTitle>{t("packageSelection.activeDevices")}</SectionTitle>
        <LoadingContainer message={t("packageSelection.loading")} />
      </Card>
    );
  }

  const currentExtraQuantity = extraDeviceItem
    ? getQuantityOnDate(billingItems ?? [], extraDeviceItem.id, today)
    : 0;
  const currentTotalDevices = BASE_INCLUDED_DEVICES + currentExtraQuantity;
  const timeline = extraDeviceItem
    ? getQuantityTimeline(billingItems ?? [], extraDeviceItem.id)
    : [];
  const futureChanges = timeline.filter((entry) => entry.startDate > today);
  const pricePerDevice = extraDeviceItem?.pricing?.monthlyPrice?.currencies?.[
    Currency.SEK
  ] ?? { amount: 0, currency: Currency.SEK };
  const extraGrossAmount = pricePerDevice.amount * currentExtraQuantity;
  const extraDiscountAmount = extraDeviceBillingItem?.discount?.amount ?? 0;
  const extraNetAmount = Math.max(0, extraGrossAmount - extraDiscountAmount);

  return (
    <Card>
      <SectionHeader>
        <SectionTitle>{t("packageSelection.activeDevices")}</SectionTitle>
        {extraDeviceItem && (
          <Button onClick={() => setIsChangeOpen(true)}>
            {t("packageSelection.changeDevices")}
          </Button>
        )}
      </SectionHeader>

      {extraDeviceItem || currentTotalDevices > 0 ? (
        <Summary>
          <SummaryValue>
            {t("packageSelection.totalDevices", {
              count: currentTotalDevices,
            })}
          </SummaryValue>
          <BreakdownList>
            <BreakdownItem>
              <span>
                {t("packageSelection.includedInPackage", {
                  count: BASE_INCLUDED_DEVICES,
                })}
              </span>
              <BreakdownValue>
                {t("packageSelection.includedPrice")}
              </BreakdownValue>
            </BreakdownItem>
            <BreakdownItem>
              <span>
                {t("packageSelection.extraDevicesBillable", {
                  count: currentExtraQuantity,
                })}
              </span>
              <BreakdownValue>
                {currentExtraQuantity > 0
                  ? (
                      <>
                        {t("packageSelection.extraDevicesCost", {
                          price: formatMoney(pricePerDevice),
                          total: formatMoney({
                            amount: extraNetAmount,
                            currency: pricePerDevice.currency,
                          }),
                        })}
                        {extraDiscountAmount > 0 && (
                          <>
                            {" "}
                            {t("packageSelection.discountOff", {
                              amount: formatMoney({
                                amount: extraDiscountAmount,
                                currency: pricePerDevice.currency,
                              }),
                            })}
                          </>
                        )}
                      </>
                    )
                  : t("packageSelection.noExtraDevicesCost")}
              </BreakdownValue>
            </BreakdownItem>
          </BreakdownList>
          {futureChanges.map((entry) => (
            <SummaryMeta key={entry.startDate}>
              {t("packageSelection.scheduledDeviceChange", {
                count: BASE_INCLUDED_DEVICES + entry.quantity,
                date: formatDate(entry.startDate),
              })}
            </SummaryMeta>
          ))}
        </Summary>
      ) : (
        <EmptyText>{t("packageSelection.noExtraDevices")}</EmptyText>
      )}

      {extraDeviceItem && (
        <QuantityBillingItemEditModal
          isOpen={isChangeOpen}
          onClose={() => setIsChangeOpen(false)}
          title={t("packageSelection.changeDevices")}
          billingItems={billingItems}
          billingItem={extraDeviceBillingItem}
          availableBillingItemId={extraDeviceItem.id}
          quantityLabel={t("packageSelection.deviceCount")}
          defaultEffectiveFrom={startDate}
          quantityOffset={BASE_INCLUDED_DEVICES}
          minDisplayQuantity={BASE_INCLUDED_DEVICES}
          infoMessage={t("packageSelection.deviceChangeInfo", {
            count: BASE_INCLUDED_DEVICES,
          })}
          previewTitle={t("packageSelection.deviceChangePreview")}
          timelineLabel={t("packageSelection.deviceQuantityHistory")}
          formatTimelineQuantity={(quantity) =>
            BASE_INCLUDED_DEVICES + quantity
          }
          renderPreview={(billingQuantity) => {
            const extraMonthlyCost = {
              amount: Math.max(
                0,
                pricePerDevice.amount * billingQuantity - extraDiscountAmount
              ),
              currency: pricePerDevice.currency,
            };
            return (
              <>
                <BreakdownItem>
                  <span>
                    {t("packageSelection.includedInPackage", {
                      count: BASE_INCLUDED_DEVICES,
                    })}
                  </span>
                  <BreakdownValue>
                    {t("packageSelection.includedPrice")}
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <span>
                    {t("packageSelection.extraDevicesBillable", {
                      count: billingQuantity,
                    })}
                  </span>
                  <BreakdownValue>
                    {billingQuantity > 0
                      ? (
                          <>
                            {t("packageSelection.extraDevicesCost", {
                              price: formatMoney(pricePerDevice),
                              total: formatMoney(extraMonthlyCost),
                            })}
                            {extraDiscountAmount > 0 && (
                              <>
                                {" "}
                                {t("packageSelection.discountOff", {
                                  amount: formatMoney({
                                    amount: extraDiscountAmount,
                                    currency: pricePerDevice.currency,
                                  }),
                                })}
                              </>
                            )}
                          </>
                        )
                      : t("packageSelection.noExtraDevicesCost")}
                  </BreakdownValue>
                </BreakdownItem>
              </>
            );
          }}
        />
      )}
    </Card>
  );
};
